/**
 * BudgetsPage — budżety per kategoria, progres bar zielony→żółty→czerwony.
 * Pełny CRUD modal — Faza 6.5. Tutaj read-only listing dla MVP rebrandu.
 */
import { useMemo, useState } from 'react';
import { Icons } from '../components/Icons';
import { useBudgetsStore, type Budget } from '../features/budgets/budgetsStore';
import { useCategoriesStore } from '../features/categories/categoriesStore';
import { useTransactionsStore } from '../features/transactions/transactionsStore';
import { BudgetEditModal } from '../components/budgets/BudgetEditModal';
import { formatCurrency, safeColor } from '../lib/utils';
import { getCategoryIcon } from '../lib/categoryIcons';

export function BudgetsPage() {
  const budgets = useBudgetsStore((s) => s.budgets);
  const categories = useCategoriesStore((s) => s.categories);
  const transactions = useTransactionsStore((s) => s.transactions);
  const [editing, setEditing] = useState<Budget | 'new' | null>(null);

  // Bieżący miesiąc — suma wydatków per category
  const spentByCategory = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    const map = new Map<string, number>();
    for (const t of transactions) {
      if (t.type !== 'expense') continue;
      if (t.date < start || t.date > end) continue;
      if (!t.categoryId) continue;
      map.set(t.categoryId, (map.get(t.categoryId) ?? 0) + t.amount);
    }
    return map;
  }, [transactions]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white">Budżety</h1>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-3 py-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold flex items-center gap-1.5 shadow-card transition"
        >
          <Icons.Plus size={16} strokeWidth={2.5} />
          Nowy
        </button>
      </div>

      {budgets.length === 0 ? (
        <div className="text-center py-12">
          <Icons.ChartPie size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-slate-500 dark:text-slate-400 mb-1">Brak budżetów</p>
          <p className="text-sm text-slate-400 dark:text-slate-500">Utwórz pierwszy budżet kategorii</p>
        </div>
      ) : (
        <div className="space-y-3">
          {budgets.map((b) => {
            const cat = categories.find((c) => c.id === b.categoryId);
            const Icon = getCategoryIcon(cat?.icon);
            const color = safeColor(cat?.color ?? '#94a3b8', '#94a3b8');
            const spent = spentByCategory.get(b.categoryId) ?? 0;
            const pct = b.amount > 0 ? Math.min(150, Math.round((spent / b.amount) * 100)) : 0;
            const status = pct >= 100 ? 'danger' : pct >= 80 ? 'warn' : 'ok';
            const barColor = status === 'danger' ? 'bg-rose-500' : status === 'warn' ? 'bg-amber-500' : 'bg-emerald-500';
            const remaining = Math.max(0, b.amount - spent);

            return (
              <button
                key={b.id}
                type="button"
                onClick={() => setEditing(b)}
                className="w-full text-left bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-4 hover:border-sky-300 dark:hover:border-sky-500/40 transition"
              >
                <div className="flex items-start gap-3">
                  <span
                    className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${color}22`, color }}
                  >
                    <Icon size={20} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{cat?.name ?? 'Kategoria'}</p>
                      <span className={`text-xs font-bold tabular-nums ${status === 'danger' ? 'text-rose-600 dark:text-rose-300' : status === 'warn' ? 'text-amber-600 dark:text-amber-300' : 'text-emerald-600 dark:text-emerald-300'}`}>{pct}%</span>
                    </div>
                    <div className="h-2 mt-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="flex items-baseline justify-between mt-2 text-xs">
                      <span className="text-slate-500 dark:text-slate-400 tabular-nums">
                        {formatCurrency(spent)} / {formatCurrency(b.amount)}
                      </span>
                      <span className="text-slate-400 tabular-nums">
                        {status === 'danger' ? `Przekroczono o ${formatCurrency(spent - b.amount)}` : `Pozostało ${formatCurrency(remaining)}`}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {editing && (
        <BudgetEditModal
          budget={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
